/* ============================================================
   SHIVOO STUDIOS — MiniMonster (standalone, scroll-driven)
   Single persistent canvas, one camera. The machine's animation,
   rotation and the four numbered service markers are ALL driven by
   scroll. The stage tilts with the mouse. Tap a revealed marker to
   load that part's detail into the side panel. No free-drag, no idle.
   ============================================================ */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

const asset = (p) => import.meta.env.BASE_URL + p.replace(/^\//, '');

/* ---------- palette ---------- */
const COL = { ink: 0x232624, putty: 0xE6E1D7, light: 0xF4F2EE, accent: 0xC8552E, brass: 0xB98A4B, warm: 0xECE6DA, cool: 0xCDC8BC };

/* ---------- math helpers ---------- */
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
const range = (x, a, b) => clamp((x - a) / (b - a), 0, 1);

/* ---------- PART DATA ----------
   `a`   = anchor ON the part (model-local, 2.3-normalised frame)
   `off` = vector from the part out to the floating numbered disc
   anchor positions are best-guess to the matching part of the machine and are
   easy to nudge (just edit `a` / `off`).                                      */
// `track` pins the marker to a real mesh node (by glTF node name) so it rides
// the actual part as the machine animates; `a` is a fallback if the node isn't
// found. `off` is small and biased toward the camera so discs hug the machine
// and stay on screen.
const PARTS = [
  { n: 1, a: new THREE.Vector3(-0.02, -0.70, -0.27), off: new THREE.Vector3(0.05, -0.29, 0.42),
    tag: 'WORKHOLDING', title: 'Access Options',
    bullets: ['BasePlates have 7 positions', 'Wide variety of 52mm tooling capability', 'Vises', 'Collet chucks', 'Angle blocks', 'Vacuum fixtures'] },
  { n: 2, track: 'MM_Part.183_LOD1', trackGeomCenter: true, a: new THREE.Vector3(-0.61, 0.38, 0.78), off: new THREE.Vector3(0.30, 0.62, 0.30),
    tag: 'TOOLING', title: 'Turret Options',
    bullets: ['Holds 10 tools', 'HSKE25'] },
  { n: 3, a: new THREE.Vector3(0.34, -0.26, -0.76), off: new THREE.Vector3(0.42, -0.25, -0.13),
    tag: 'ROTARY', title: '4th Axis Options',
    sub: 'Harmonic Drive Unit',
    bullets: ['Under 10 sec arc backlash', 'Up to 40 Nm continuous torque', '52mm zero-point compatible'] },
  { n: 4, a: new THREE.Vector3(-0.55, -0.41, -0.35), off: new THREE.Vector3(0.34, 0.14, 0.14),
    tag: 'METROLOGY', title: 'Laser Tool Setter',
    body: 'Measures tools up to 8mm in diameter with 1-micron precision.' },
  { n: 5, track: 'MM_Part.244_LOD1', trackGeomCenter: true, anchorOffset: new THREE.Vector3(0, 0.11, 0.03), a: new THREE.Vector3(-0.61, 0.16, 0.45), off: new THREE.Vector3(-0.68, 0.18, 0.30),
    tag: 'SPINDLE', title: 'Spindle Options',
    sub: 'HSK25KE · ISO20',
    bullets: ['4 kW power', '40,000 RPM'] },
];

// render a part's body: optional sub-line, then bullets or a plain line
function bodyHTML(p) {
  let h = '';
  if (p.sub) h += `<span class="detail-sub">${p.sub}</span>`;
  if (p.bullets) h += '<ul class="detail-list">' + p.bullets.map((x) => `<li>${x}</li>`).join('') + '</ul>';
  if (p.body) h += `<span class="detail-line">${p.body}</span>`;
  return h;
}

/* ---------- loaders ---------- */
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(asset('draco/'));
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);
function loadGLB(path, onFrac) {
  return new Promise((res, rej) => gltfLoader.load(path, res, (xhr) => { if (onFrac && xhr.total) onFrac(xhr.loaded / xhr.total); }, rej));
}

/* ---------- normalize (center + scale) ---------- */
function normalize(obj, target = 3) {
  const box = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const s = target / (Math.max(size.x, size.y, size.z) || 1);
  obj.scale.setScalar(s);
  obj.position.copy(center).multiplyScalar(-s);
  return { size, center, scale: s };
}

/* ---------- numbered disc texture ---------- */
function discTexture(n) {
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const g = c.getContext('2d');
  // solid terracotta disc + white digit — identical to the panel's .num-chip
  g.fillStyle = '#C8552E'; g.beginPath(); g.arc(64, 64, 60, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#F4F2EE'; g.font = '700 64px "Space Grotesk", sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(n), 64, 68);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;   // without this the terracotta renders washed-out/light
  return tex;
}

/* ============================================================
   Scene base
   ============================================================ */
class Scene {
  constructor(engine, sectionId) {
    this.engine = engine;
    this.section = document.getElementById(sectionId);
    this.rail = new THREE.Group();
    this.group = new THREE.Group();
    this.rail.add(this.group);
    this.rail.visible = false;
    engine.stage.add(this.rail);
    this.loaded = false; this.loading = false;
    this.enterFrom = -0.55; this.enterTo = 0.10; this.enterH = 3.7; this.exitH = 3.7;
    this.top = 0; this.scrollable = 1;   // cached scroll geometry (set by measure)
  }
  async ensureLoaded() {
    if (this.loaded || this.loading) return;
    this.loading = true;
    try { await this.load(); } catch (e) { console.error('scene load failed', e); }
    this.loaded = true;
  }
  async load() {}
  update() {}
}

/* ============================================================
   MiniMonster scene
   ============================================================ */
class MinimonsterScene extends Scene {
  async load() {
    // hidden at the very top, then rises THROUGH the hero so it's basically
    // seated by the time the hero scrolls away — no blank screen in between.
    this.enterFrom = -0.24; this.enterTo = -0.05; this.enterH = 4.3; this.exitH = 4.5;
    const gltf = await loadGLB(asset('models/minimonster.glb'), (f) => this.engine.setProg('mini', f));
    this.model = gltf.scene;
    normalize(this.model, 2.3);
    this.model.traverse((o) => {
      const nm = ((o.name || '') + ' ' + (o.parent ? o.parent.name : '')).toLowerCase();
      if (nm.includes('dont render') || nm.includes("don't render")) o.visible = false;
    });
    this.group.add(this.model);
    this.group.position.x = -0.7;       // sit left of centre (panel is on the right)

    if (gltf.animations && gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(this.model);
      this.clip = gltf.animations[0];
      this.action = this.mixer.clipAction(this.clip);
      this.action.play(); this.action.paused = true;
    }

    // markers: small disc + thin cylinder leader, revealed sequentially by scroll.
    // each pip can pin to a real mesh node so it rides the part as it animates.
    this.pips = []; this.pickables = [];
    const rodGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
    this.model.updateMatrixWorld(true);
    // three's GLTFLoader strips reserved chars (esp. '.') from node names —
    // match that when resolving track targets.
    const sanitize = (s) => s.replace(/\s/g, '_').replace(/[\[\]\.:\/]/g, '');
    PARTS.forEach((p, i) => {
      const spr = new THREE.Sprite(new THREE.SpriteMaterial({ map: discTexture(p.n), transparent: true, opacity: 0, depthTest: true, depthWrite: false, toneMapped: false }));
      spr.scale.setScalar(0.001);
      spr.renderOrder = 3;
      spr.userData.part = p; spr.userData.revealed = false;
      const rod = new THREE.Mesh(rodGeo, new THREE.MeshBasicMaterial({ color: COL.accent, transparent: true, opacity: 0, toneMapped: false }));
      this.group.add(spr); this.group.add(rod);
      // resolve the tracked node (if any) and its local centre
      let trackNode = null, trackCenter = null;
      if (p.track) {
        trackNode = this.model.getObjectByName(p.track) || this.model.getObjectByName(sanitize(p.track));
        if (trackNode) {
          trackCenter = new THREE.Vector3();
          if (p.trackGeomCenter && trackNode.isMesh && trackNode.geometry) {
            // the mesh's own geometry centre (sits on the part face) — used for
            // the turret, whose subtree bbox drifts toward its shaft
            if (!trackNode.geometry.boundingBox) trackNode.geometry.computeBoundingBox();
            trackNode.geometry.boundingBox.getCenter(trackCenter);
          } else {
            const box = new THREE.Box3().setFromObject(trackNode);   // subtree bbox centre (default)
            if (!box.isEmpty()) { box.getCenter(trackCenter); trackNode.worldToLocal(trackCenter); }
          }
        } else {
          console.warn('marker', p.n, 'track node not found:', p.track);
        }
      }
      this.pips.push({ spr, rod, a: p.a, off: p.off, start: 0.26 + i * 0.12, trackNode, trackCenter,
        anchorOffset: p.anchorOffset ? p.anchorOffset.clone() : new THREE.Vector3() });
      this.pickables.push(spr);
    });
    this._pa = new THREE.Vector3(); this._pb = new THREE.Vector3(); this._pd = new THREE.Vector3(); this._pu = new THREE.Vector3(0, 1, 0);
    this._off = new THREE.Vector3(); this._qBase = new THREE.Quaternion(); this._qFullInv = new THREE.Quaternion(); this._eBase = new THREE.Euler();
    this.group.rotation.y = 0.4;
    this.manualYaw = 0; this.manualPitch = 0; this.dragVelY = 0; this.dragVelX = 0;   // free-rotate state
  }

  update(t, dt, time, mx, my, raw) {
    // responsive placement: left of the side panel on desktop; centred, lifted
    // and a touch smaller on mobile where the panel is a bottom sheet
    const mobile = window.innerWidth <= 768;
    this.group.position.x = mobile ? 0 : -0.7;
    this.group.position.y = mobile ? 0.55 : 0;
    this.group.scale.setScalar(mobile ? 0.82 : 1);
    // slow dolly toward the camera as the scene plays
    this.group.position.z = lerp(-0.5, 0.7, t);

    // scrub the embedded machine animation across the whole viewport passage
    const u = range(raw != null ? raw : t, -0.55, 1.45);
    if (this.action && this.clip) { this.action.time = u * this.clip.duration; this.mixer.update(0); }

    // free-rotate: manual yaw/pitch added on top of the scroll orientation.
    // the manual offset PERSISTS (it is never eased back) so "Done" leaves the
    // model at the new angle; scrolling then scrubs the tour from there.
    const rotating = this.engine.rotateMode;
    if (rotating && !this.engine.dragging) {   // inertia glide on release
      this.manualYaw += this.dragVelY;
      this.manualPitch = clamp(this.manualPitch + this.dragVelX, -0.78, 0.78);
      this.dragVelY *= 0.92; this.dragVelX *= 0.92;
    }
    this.group.rotation.y = (0.4 - u * 0.9) + this.manualYaw;
    this.group.rotation.x = this.manualPitch;

    if (!this.pips) return;
    this.pips.forEach((p, i) => {
      // reveal is scroll-driven; in rotate mode scroll is frozen, so only the
      // callouts already revealed stay visible (the rest remain hidden)
      const w = smooth(range(t, p.start, p.start + 0.08));
      // pinned markers ride their real mesh node (live, follows the animation);
      // a manual marker (or one with no track node) uses its fixed anchor `a`
      if (p.trackNode && !p.manual) {
        this._pa.copy(p.trackCenter);
        p.trackNode.localToWorld(this._pa);
        this.group.worldToLocal(this._pa);
        this._pa.add(p.anchorOffset);   // relative nudge while staying linked
      } else this._pa.copy(p.a);
      // offset is model-fixed: the leader cylinder rotates with the machine;
      // only the disc (a sprite) faces the camera
      this._pb.copy(this._pa).add(p.off);
      p.spr.position.copy(this._pb);
      p.spr.material.opacity = w;
      p.spr.scale.setScalar(Math.max(0.001, 0.264 * w));     // numbered discs (20% larger)
      p.spr.userData.revealed = w > 0.6;
      // thin leader rod, from the part toward the disc, stopping at the disc rim
      this._pd.copy(this._pb).sub(this._pa);
      const full = Math.max(0.001, this._pd.length() - 0.12);
      this._pd.setLength(full * w);
      const len = this._pd.length();
      p.rod.visible = len > 0.01 && w > 0.02;
      if (p.rod.visible) {
        p.rod.position.copy(this._pa).addScaledVector(this._pd, 0.5);
        p.rod.quaternion.setFromUnitVectors(this._pu, this._pd.clone().normalize());
        p.rod.scale.set(0.008, len, 0.008);                  // thin cylinder
        p.rod.material.opacity = w;
      }
    });
  }
}

/* ============================================================
   Engine
   ============================================================ */
class Engine {
  constructor() {
    this.canvas = document.getElementById('bg-canvas');
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.25;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.camera = new THREE.PerspectiveCamera(42, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, 0, 6.4);

    this.world = new THREE.Scene();
    this.addLights();
    this.stage = new THREE.Group();        // tilts with the mouse
    this.world.add(this.stage);

    this.clock = new THREE.Clock();
    this.mx = 0; this.my = 0; this.tmx = 0; this.tmy = 0;

    this.prog = {};
    this.loaderEl = document.getElementById('loader');
    this.fillEl = document.getElementById('loader-progress-fill');
    this.pctEl = document.getElementById('loader-percentage-num');
    this.railEl = document.getElementById('scroll-rail-fill');
    this.loaderDone = false;

    this.panel = document.getElementById('detail-panel');
    this.elTag = document.getElementById('detail-tag');
    this.elTitle = document.getElementById('detail-title');
    this.elBody = document.getElementById('detail-body');
    this.selected = -1;

    // free-rotate toggle
    this.rotBtn = document.getElementById('rotate-toggle');
    this.rotLabel = this.rotBtn ? this.rotBtn.querySelector('.rt-label') : null;
    this.rotHint = document.getElementById('rotate-hint');
    this.rotateMode = false; this.dragging = false;
    if (this.rotBtn) this.rotBtn.addEventListener('click', () => this.toggleRotate());

    this._wp = new THREE.Vector3();

    // the inner scroll container (body itself doesn't scroll)
    this.scroller = document.getElementById('scroller');
    this.baseVH = this.scroller.clientHeight || window.innerHeight;
    this.baseVW = window.innerWidth;
    this.lockHeights();

    this.scenes = [];
    this.bindEvents();
  }

  lockHeights() {
    const vh = this.baseVH, mobile = this.baseVW <= 768, mult = mobile ? 4.2 : 3.6;
    const hero = document.getElementById('section-hero');
    const sec = document.getElementById('section-minimonster');
    const sticky = sec ? sec.querySelector('.sticky-stage') : null;
    if (hero) hero.style.minHeight = vh + 'px';
    if (sec) sec.style.height = Math.round(vh * mult) + 'px';
    if (sticky) sticky.style.height = vh + 'px';
    this.measure();
  }

  // cache each scene's scroll geometry (offset within the scroll container)
  measure() {
    if (!this.scenes || !this.scroller) return;
    const base = this.scroller.getBoundingClientRect().top;
    for (const sc of this.scenes) {
      const r = sc.section.getBoundingClientRect();
      sc.top = r.top - base + this.scroller.scrollTop;
      sc.scrollable = Math.max(1, sc.section.offsetHeight - this.baseVH);
    }
  }

  addLights() {
    // values tuned live via the dev lighting panel
    this.ambient = new THREE.AmbientLight(0xddddd0, 0.51); this.world.add(this.ambient);
    this.key = new THREE.DirectionalLight(0xffffff, 4.89); this.key.position.set(3.3, 8, 6); this.world.add(this.key);
    this.fill = new THREE.DirectionalLight(0xcdc8bc, 1.49); this.fill.position.set(-6, 3, -4); this.world.add(this.fill);
    this.rim = new THREE.PointLight(0x99caff, 2.24, 10); this.rim.position.set(-1.1, 0, 3); this.world.add(this.rim);
  }

  // live lighting tweak panel — DEV ONLY (lil-gui loaded from CDN so it never
  // ends up in the production build). Hit "copy values" and paste them to bake in.
  async setupLightGUI() {
    let GUI;
    try { const mod = await import('https://cdn.jsdelivr.net/npm/lil-gui@0.20/+esm'); GUI = mod.GUI || mod.default; }
    catch (e) { console.warn('lil-gui failed to load:', e); return; }
    if (!GUI) return;
    const gui = new GUI({ title: 'Lighting (dev)' });
    gui.add(this.renderer, 'toneMappingExposure', 0, 3, 0.01).name('exposure');
    const addLight = (name, light, maxI) => {
      const f = gui.addFolder(name);
      f.add(light, 'intensity', 0, maxI, 0.01);
      const proxy = { color: '#' + light.color.getHexString() };
      f.addColor(proxy, 'color').onChange((v) => light.color.set(v));
      if (light.position && name !== 'Ambient') {
        f.add(light.position, 'x', -14, 14, 0.1);
        f.add(light.position, 'y', -14, 14, 0.1);
        f.add(light.position, 'z', -14, 14, 0.1);
      }
      if ('distance' in light) f.add(light, 'distance', 0, 40, 0.5);
      f.close();
    };
    addLight('Ambient', this.ambient, 4);
    addLight('Key', this.key, 6);
    addLight('Fill', this.fill, 4);
    addLight('Rim', this.rim, 6);
    gui.add({ copy: () => this.dumpLights() }, 'copy').name('▶ copy values to console');
  }

  dumpLights() {
    const f = (l) => ({ intensity: +l.intensity.toFixed(2), color: '#' + l.color.getHexString(),
      pos: l.position ? [+l.position.x.toFixed(1), +l.position.y.toFixed(1), +l.position.z.toFixed(1)] : undefined,
      distance: 'distance' in l ? +l.distance.toFixed(1) : undefined });
    console.log('LIGHTING\n' + JSON.stringify({
      exposure: +this.renderer.toneMappingExposure.toFixed(2),
      ambient: f(this.ambient), key: f(this.key), fill: f(this.fill), rim: f(this.rim),
    }, null, 2));
  }

  // live MARKER position editor — DEV / ?dev=true. Lets you set each marker's
  // anchor (where the leader points) and disc offset by XYZ. Tip: turn the model
  // to a straight-on view (don't rotate) so the axes line up: +x right, +y up,
  // +z toward you. Toggle "manual" to override a part-tracked marker.
  async setupMarkerGUI() {
    if (!this.mini || !this.mini.pips) { setTimeout(() => this.setupMarkerGUI(), 500); return; }
    let GUI;
    try { const mod = await import('https://cdn.jsdelivr.net/npm/lil-gui@0.20/+esm'); GUI = mod.GUI || mod.default; }
    catch (e) { return; }
    if (!GUI) return;
    const gui = new GUI({ title: 'Marker positions (dev)' });
    gui.domElement.style.left = '12px'; gui.domElement.style.right = 'auto';   // left side, clear of the lighting panel
    const grp = this.mini.group;
    this.mini.pips.forEach((p) => {
      const f = gui.addFolder('Marker ' + p.spr.userData.part.n);
      const linked = !!p.trackNode;   // 2 & 5: anchor rides the part, sliders nudge it RELATIVELY
      const aTarget = linked ? p.anchorOffset : p.a;
      const aName = linked ? 'anchor Δ' : 'anchor ';   // Δ = relative
      f.add(aTarget, 'x', -1.6, 1.6, 0.01).name(aName + 'x');
      f.add(aTarget, 'y', -1.6, 1.6, 0.01).name(aName + 'y');
      f.add(aTarget, 'z', -1.6, 1.6, 0.01).name(aName + 'z');
      f.add(p.off, 'x', -1.6, 1.6, 0.01).name('disc x');
      f.add(p.off, 'y', -1.6, 1.6, 0.01).name('disc y');
      f.add(p.off, 'z', -1.6, 1.6, 0.01).name('disc z');
      f.close();
    });
    gui.add({ copy: () => this.dumpMarkers() }, 'copy').name('▶ copy values to console');
  }

  dumpMarkers() {
    const v = (x) => [+x.x.toFixed(2), +x.y.toFixed(2), +x.z.toFixed(2)];
    const out = this.mini.pips.map((p) => {
      const linked = !!p.trackNode;
      const o = { n: p.spr.userData.part.n, linked, off: v(p.off) };
      if (linked) o.anchorOffset = v(p.anchorOffset); else o.a = v(p.a);
      return o;
    });
    console.log('MARKERS\n' + JSON.stringify(out, null, 2));
  }

  register(scene) { this.scenes.push(scene); }

  setProg(key, frac) {
    this.prog[key] = clamp(frac, 0, 1);
    if (this.loaderDone) return;
    const pct = Math.min(100, Math.round((this.prog.mini || 0) * 100));
    if (this.fillEl) this.fillEl.style.width = pct + '%';
    if (this.pctEl) this.pctEl.textContent = pct;
    if (pct >= 100) { this.loaderDone = true; setTimeout(() => this.loaderEl.classList.add('is-loaded'), 400); }
  }

  bindEvents() {
    window.addEventListener('mousemove', (e) => {
      this.tmx = (e.clientX / window.innerWidth) * 2 - 1;
      this.tmy = -(e.clientY / window.innerHeight) * 2 + 1;
    });
    window.addEventListener('resize', () => {
      const w = window.innerWidth, h = window.innerHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h);
      // body doesn't scroll, so the toolbar stays put and clientHeight is stable;
      // re-lock on any real size change (orientation etc.)
      if (Math.abs(w - this.baseVW) > 2 || Math.abs((this.scroller.clientHeight || h) - this.baseVH) > 2) {
        this.baseVW = w; this.baseVH = this.scroller.clientHeight || h; this.lockHeights();
      }
    });

    // tap a revealed marker -> load its detail. Pointer events unify mouse and
    // touch; we only treat a near-stationary, quick press as a tap (so a scroll
    // drag never fires a selection). Hit-test by projecting each disc to the
    // screen and measuring pixel distance — far more forgiving on a phone than
    // a pixel-perfect sprite raycast, and immune to body occlusion.
    let dx0 = 0, dy0 = 0, dt0 = 0, px = 0, py = 0;
    const onUI = (e) => e.target.closest && e.target.closest('.detail-panel, .site-header, .site-footer, .rotate-toggle, a, button');
    window.addEventListener('pointerdown', (e) => {
      dx0 = e.clientX; dy0 = e.clientY; dt0 = performance.now(); px = e.clientX; py = e.clientY;
      if (this.rotateMode && !onUI(e)) {
        this.dragging = true;
        if (this.mini) { this.mini.dragVelY = 0; this.mini.dragVelX = 0; }
        if (this.rotHint) this.rotHint.classList.remove('is-shown');
      }
    });
    window.addEventListener('pointermove', (e) => {
      if (!this.dragging || !this.mini) { px = e.clientX; py = e.clientY; return; }
      const ddx = e.clientX - px, ddy = e.clientY - py; px = e.clientX; py = e.clientY;
      this.mini.manualYaw += ddx * 0.01;
      this.mini.manualPitch = clamp(this.mini.manualPitch + ddy * 0.008, -0.78, 0.78);
      this.mini.dragVelY = ddx * 0.01; this.mini.dragVelX = ddy * 0.008;
    });
    const endDrag = () => { this.dragging = false; };
    window.addEventListener('pointercancel', endDrag);
    window.addEventListener('pointerup', (e) => {
      this.dragging = false;
      // a quick, stationary press is a tap (selects a callout) in BOTH modes;
      // a drag rotates (rotate mode) or scrolls (normal) and is not a tap
      const tap = performance.now() - dt0 < 500 && Math.hypot(e.clientX - dx0, e.clientY - dy0) < 12;
      dt0 = 0;
      if (!tap || e.clientX == null) return;
      if (onUI(e)) return;
      this.pickMarker(e.clientX, e.clientY);
    });
  }

  toggleRotate() {
    this.rotateMode = !this.rotateMode;
    const m = this.rotateMode;
    this.scroller.classList.toggle('rotate-mode', m);
    if (this.rotBtn) { this.rotBtn.classList.toggle('is-active', m); this.rotBtn.setAttribute('aria-pressed', String(m)); }
    if (this.rotLabel) this.rotLabel.textContent = m ? 'Done' : 'Rotate model';
    if (m) {
      if (this.rotHint) this.rotHint.classList.add('is-shown');   // keep callouts + panel visible
    } else {
      this.dragging = false;
      if (this.rotHint) this.rotHint.classList.remove('is-shown');
    }
  }

  pickMarker(cx, cy) {
    const sc = this.mini;
    if (!sc || !sc.loaded || !sc.rail.visible || !sc.pickables) return;
    const R = Math.max(34, Math.min(window.innerWidth, window.innerHeight) * 0.07);   // generous touch radius
    let best = null, bestD = R;
    for (const spr of sc.pickables) {
      if (!spr.userData.revealed) continue;
      spr.getWorldPosition(this._wp).project(this.camera);
      if (this._wp.z > 1) continue;   // behind the camera
      const sx = (this._wp.x * 0.5 + 0.5) * window.innerWidth;
      const sy = (-this._wp.y * 0.5 + 0.5) * window.innerHeight;
      const d = Math.hypot(sx - cx, sy - cy);
      if (d < bestD) { bestD = d; best = spr; }
    }
    if (best) this.select(best.userData.part);
  }

  // soft, elegant blip synthesised on the fly (no asset). Created inside the
  // tap gesture so iOS autoplay policy allows it.
  blip() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.actx = this.actx || new Ctx();
      if (this.actx.state === 'suspended') this.actx.resume();
      const t = this.actx.currentTime;
      const o = this.actx.createOscillator(), g = this.actx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(660, t);
      o.frequency.exponentialRampToValueAtTime(990, t + 0.06);   // gentle upward chirp
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.13, t + 0.012);      // soft attack
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);     // quick, clean decay
      o.connect(g).connect(this.actx.destination);
      o.start(t); o.stop(t + 0.24);
    } catch (e) { /* audio unavailable — ignore */ }
  }

  select(p) {
    if (this.selected === p.n) return;
    this.selected = p.n;
    this.blip();
    const panel = this.panel;
    // lock the current height so the box can animate to the next one
    const from = panel.getBoundingClientRect().height;
    panel.style.height = from + 'px';
    panel.classList.add('is-swapping');           // fade the text out
    clearTimeout(this._swapT); clearTimeout(this._htT);
    this._swapT = setTimeout(() => {
      this.elTag.textContent = p.tag;
      this.elTitle.innerHTML = `<span class="num-chip">${p.n}</span>${p.title}`;
      this.elBody.innerHTML = bodyHTML(p);
      // measure the new natural height, then animate height: from -> to
      panel.style.height = 'auto';
      const to = panel.getBoundingClientRect().height;
      panel.style.height = from + 'px';
      void panel.offsetHeight;                     // force reflow so the next set transitions
      panel.style.height = to + 'px';
      panel.classList.remove('is-swapping');        // fade the text in
      // release back to auto once the height transition has finished
      this._htT = setTimeout(() => { panel.style.height = ''; }, 460);
    }, 240);
  }

  sectionRaw(section) {
    const rect = section.getBoundingClientRect();
    const scrollable = rect.height - this.baseVH;   // frozen height — toolbar-proof
    if (scrollable <= 0) return rect.top < 0 ? 1 : 0;
    return -rect.top / scrollable;
  }

  resetDetail() {
    this.selected = -1;
    clearTimeout(this._swapT); clearTimeout(this._htT);
    this.panel.style.height = '';   // release any locked height
    this.elTag.textContent = 'GUIDED FEATURE CALLOUTS';
    this.elTitle.textContent = 'Tap a number.';
    this.elBody.textContent = 'Highlight specific information through an embedded, interactive, immersive and memorable experience.';
  }

  start() { this.clock.start(); this.measure(); this.loop(); }

  loop() {
    requestAnimationFrame(() => this.loop());
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const time = this.clock.elapsedTime;

    // mouse tilt of the whole stage (same feel as the main site) — frozen while
    // free-rotating so the drag has full control
    this.mx += (this.tmx - this.mx) * 0.05;
    this.my += (this.tmy - this.my) * 0.05;
    if (!this.rotateMode) {
      this.stage.rotation.y = this.mx * 0.20;
      this.stage.rotation.x = -this.my * 0.14;
    }

    // SMOOTHED scroll: native scrolling on iOS feeds rAF in jittery bursts, and
    // a fast flick jumps raw far in one frame (the "snap"). Damping the value the
    // 3D reads makes the scrub buttery and removes the fast-scroll snap.
    // light damping for buttery scrubbing (the body no longer scrolls, so there's
    // no toolbar jump to fight — keep it gentle to stay responsive)
    const sTop = this.scroller ? this.scroller.scrollTop : window.scrollY;
    if (this.scrollSmooth == null) this.scrollSmooth = sTop;
    this.scrollSmooth += (sTop - this.scrollSmooth) * 0.3;
    if (Math.abs(sTop - this.scrollSmooth) < 0.4) this.scrollSmooth = sTop;

    const docH = this.scroller ? (this.scroller.scrollHeight - this.scroller.clientHeight) : 1;
    if (this.railEl) this.railEl.style.width = (clamp(sTop / docH, 0, 1) * 100) + '%';

    let panelOn = false, sceneInView = false;
    for (const sc of this.scenes) {
      const raw = (this.scrollSmooth - sc.top) / sc.scrollable;       // smoothed -> 3D
      const rawReal = (sTop - sc.top) / sc.scrollable;                // real -> panel/visibility
      const near = rawReal > -0.6 && rawReal < 1.6;
      if (!near) { sc.rail.visible = false; continue; }
      sc.ensureLoaded();

      const enterFrom = sc.enterFrom, enterTo = sc.enterTo, enterH = sc.enterH, exitH = sc.exitH;
      let railY = 0;
      if (raw < enterTo) railY = lerp(-enterH, 0, smooth(range(raw, enterFrom, enterTo)));
      else if (raw > 0.90) railY = lerp(0, exitH, smooth(range(raw, 0.90, 1.58)));
      sc.rail.position.y = railY;
      sc.rail.visible = sc.loaded && railY > -enterH * 1.1 && railY < exitH * 1.1;

      if (sc.rail.visible) sc.update(clamp(raw, 0, 1), dt, time, this.mx, this.my, raw);
      // panel only appears once marker #1 has appeared (its reveal starts at 0.30)
      const firstStart = (sc.pips && sc.pips[0]) ? sc.pips[0].start : 0.30;
      if (rawReal > firstStart && rawReal < 0.97) panelOn = true;
      if (sc === this.mini && rawReal > 0.08 && rawReal < 0.96) sceneInView = true;
    }
    if (this.rotateMode && sceneInView) panelOn = true;   // keep panel + callouts up while inspecting
    this.panel.classList.toggle('is-visible', panelOn);
    // rewind: when the machine scrolls out of view, revert to the default prompt
    if (!panelOn && this.selected !== -1 && !this.rotateMode) this.resetDetail();
    // show the rotate toggle while the machine is in view (and keep it while rotating)
    if (this.rotBtn) this.rotBtn.classList.toggle('is-shown', sceneInView || this.rotateMode);

    this.renderer.render(this.world, this.camera);
  }
}

/* ============================================================
   Boot
   ============================================================ */
const engine = new Engine();
const mini = new MinimonsterScene(engine, 'section-minimonster');
engine.register(mini);
engine.mini = mini;
mini.ensureLoaded();

// safety: never let the loader trap the user if the asset stalls
setTimeout(() => { if (!engine.loaderDone) { engine.loaderDone = true; engine.loaderEl.classList.add('is-loaded'); } }, 12000);

engine.start();
window.__engine = engine;   // headless-verification handle; harmless in prod
window.__THREE = THREE;

// live lighting controls — always on in local dev, and on the deployed site
// when the URL has ?dev=true (so the client can tweak too). lil-gui still loads
// from CDN on demand, so it costs nothing for normal visitors.
if (import.meta.env.DEV || new URLSearchParams(location.search).get('dev') === 'true') {
  engine.setupLightGUI();
  engine.setupMarkerGUI();
}

document.getElementById('header-cta-btn')?.addEventListener('click', () => document.getElementById('section-specs').scrollIntoView({ behavior: 'smooth' }));
document.getElementById('footer-cta-link')?.addEventListener('click', (e) => { e.preventDefault(); document.getElementById('contact').scrollIntoView({ behavior: 'smooth' }); });
